import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, Bell } from "lucide-react";
import ComplaintNotifyDialog from "@/components/ComplaintNotifyDialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { GermanDateInput } from "@/components/GermanDateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ageDays = (d) => Math.floor((Date.now() - new Date(`${d}T00:00:00`).getTime()) / 86400000);
const isOverdue = (c) => !c.resolved && ageDays(c.date) > 14;

const EMPTY = { date: "", person_name: "", description: "", action: "", resolved: false };

function ComplaintDialog({ item, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (item) setF({ ...EMPTY, ...item }); }, [item]);
  const save = async () => {
    try {
      if (item.complaint_id) await api.put(`/audit/complaints/${item.complaint_id}`, f);
      else await api.post("/audit/complaints", f);
      toast.success("Beschwerde gespeichert");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="complaint-dialog">
        <DialogHeader><DialogTitle>{item?.complaint_id ? "Beschwerde bearbeiten" : "Neue Beschwerde"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <GermanDateInput value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} placeholder="Datum" data-testid="complaint-date" />
          <input value={f.person_name} onChange={(e) => setF({ ...f, person_name: e.target.value })} placeholder="Teilnehmer:in" className="input-tech" data-testid="complaint-person" />
          <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Beschreibung der Beschwerde" className="input-tech resize-none" data-testid="complaint-description" />
          <textarea rows={2} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} placeholder="Ergriffene Maßnahme" className="input-tech resize-none" data-testid="complaint-action" />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.resolved} onChange={(e) => setF({ ...f, resolved: e.target.checked })} data-testid="complaint-resolved" /> Erledigt
          </label>
          <button onClick={save} className="w-full bg-slate-900 text-white py-3 text-sm font-medium hover:bg-black" data-testid="complaint-save">Speichern</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ComplaintsRegister({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [notify, setNotify] = useState(false);
  const load = () => api.get("/audit/complaints").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (c) => {
    if (!window.confirm("Beschwerde wirklich löschen?")) return;
    await api.delete(`/audit/complaints/${c.complaint_id}`);
    load(); onChanged?.();
  };
  return (
    <div className="border border-slate-200 bg-white" data-testid="complaints-register">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Beschwerde-Register · {rows.length}
          {rows.some(isOverdue) && <span className="ml-2 px-1.5 py-0.5 bg-red-600 text-white normal-case tracking-normal" data-testid="complaints-overdue-count">{rows.filter(isOverdue).length} überfällig</span>}
        </div>
        <div className="flex gap-2 flex-wrap">
        <button onClick={() => setNotify(true)} className="px-3 py-2 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid="complaint-notify-btn"><Bell size={12} /> Benachrichtigung</button>
        <button onClick={() => setEdit({})} className="px-3 py-2 text-xs font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1.5" data-testid="complaint-new-btn"><Plus size={12} /> Beschwerde erfassen</button>
        </div>
      </div>
      {rows.length === 0 ? <div className="p-8 text-center text-sm text-slate-500" data-testid="complaints-empty">Noch keine Beschwerden erfasst.</div> : (
        <ul className="divide-y divide-slate-100">
          {rows.map((c) => (
            <li key={c.complaint_id} className={`px-5 py-3 flex items-start gap-3 ${isOverdue(c) ? "bg-red-50 border-l-4 border-red-600" : ""}`} data-testid={`complaint-row-${c.complaint_id}`}>
              <span className={`mt-0.5 px-2 py-0.5 text-[10px] font-medium shrink-0 ${c.resolved ? "bg-emerald-50 text-emerald-700" : isOverdue(c) ? "bg-red-600 text-white" : "bg-amber-50 text-amber-700"}`} data-testid={`complaint-status-${c.complaint_id}`}>
                {c.resolved ? <span className="flex items-center gap-1"><Check size={10} /> erledigt</span> : isOverdue(c) ? `überfällig · ${ageDays(c.date)} Tage` : "offen"}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900">{c.person_name} <span className="text-[11px] font-mono text-slate-400 ml-1">{formatDeShort(c.date)}</span>
                  {c.source === "bogen" && <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600" data-testid={`complaint-source-${c.complaint_id}`}>via Beschwerdebogen</span>}</div>
                <div className="text-xs text-slate-600 whitespace-pre-wrap">{c.description}</div>
                {c.action && <div className="text-xs text-slate-500 mt-1">Maßnahme: {c.action}</div>}
              </div>
              <button onClick={() => setEdit(c)} className="text-slate-400 hover:text-slate-900" data-testid={`complaint-edit-${c.complaint_id}`}><Pencil size={13} /></button>
              <button onClick={() => remove(c)} className="text-slate-400 hover:text-red-600" data-testid={`complaint-delete-${c.complaint_id}`}><Trash2 size={13} /></button>
            </li>
          ))}
        </ul>
      )}
      <ComplaintNotifyDialog open={notify} onClose={() => setNotify(false)} />
      <ComplaintDialog item={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); onChanged?.(); }} />
    </div>
  );
}
